import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { notifyUpdate } from '../../lib/api';
import { CITIES } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle2, XCircle, FileCheck } from 'lucide-react';

const ROLE_BADGES = {
  super_admin: 'bg-purple-100 text-purple-700',
  ministarstvo: 'bg-blue-100 text-blue-700',
  mup: 'bg-slate-200 text-slate-700',
  turisticka_zajednica: 'bg-teal-100 text-teal-700',
  sluzbenik: 'bg-indigo-100 text-indigo-700',
  subjekat: 'bg-orange-100 text-orange-700',
  turista: 'bg-emerald-100 text-emerald-700'
};

export default function AdminKorisnici() {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: '', city: '' });
  const canManage = hasPermission('manage_users');

  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled([
        api.get('/users'), api.get('/roles'), api.get('/change-requests')
      ]);
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : []);
      setUsers(val(results[0]));
      setRoles(val(results[1]));
      setRequests(val(results[2]));
    };
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password || !form.role) { toast.error('Popunite sva obavezna polja.'); return; }
    try {
      await api.post('/users', form);
      toast.success('Korisnik je dodan. Prilikom prve prijave mora promijeniti inicijalnu lozinku.');
      notifyUpdate();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const approve = async (id) => {
    try { await api.put(`/users/${id}/approve`); toast.success('Registracija subjekta je odobrena.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška pri odobravanju.'); }
  };

  const reject = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success('Zahtjev za registraciju je odbijen.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška.'); }
  };

  const approveRequest = async (id) => {
    try { await api.put(`/change-requests/${id}/approve`); toast.success('Izmjena podataka je odobrena.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška pri odobravanju.'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success('Korisnik je obrisan.'); notifyUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Greška pri brisanju.'); }
  };

  const pending = users.filter((u) => u.status === 'pending');
  const active = users.filter((u) => u.status === 'active');
  const pendingRequests = requests.filter((r) => r.status === 'pending');

  return (
    <div data-testid="admin-korisnici">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1a3d]">Korisnici sistema</h1>
          <p className="text-sm text-slate-500">Upravljanje korisnicima, odobravanje registracija i izmjena podataka subjekata.</p>
        </div>
        {canManage && (
          <Button onClick={() => { setForm({ name: '', email: '', password: '', role: '', city: '' }); setOpen(true); }} data-testid="add-user-btn"
            className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
            <Plus className="h-4 w-4 mr-2" /> Novi korisnik
          </Button>
        )}
      </div>

      {/* ZAHTJEVI ZA REGISTRACIJU */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="pending-registrations">
          <h2 className="font-bold text-amber-800 mb-3">Zahtjevi za registraciju subjekata ({pending.length})</h2>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="bg-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`pending-user-${u.id}`}>
                <div>
                  <p className="font-semibold text-slate-700">{u.name}</p>
                  <p className="text-xs text-slate-500">{u.email} • {u.activityCategory} • {u.city} • ID: {u.idNumber}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(u.id)} data-testid={`approve-user-${u.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-9 transition-colors duration-200">
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Odobri
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(u.id)} data-testid={`reject-user-${u.id}`}
                    className="border-red-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md h-9 transition-colors duration-200">
                    <XCircle className="h-4 w-4 mr-1.5" /> Odbij
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ZAHTJEVI ZA IZMJENU PODATAKA */}
      {pendingRequests.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6" data-testid="pending-changes">
          <h2 className="font-bold text-blue-800 mb-3">Zahtjevi za izmjenu podataka ({pendingRequests.length})</h2>
          <div className="space-y-2">
            {pendingRequests.map((r) => {
              const u = users.find((x) => x.id === r.userId);
              return (
                <div key={r.id} className="bg-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`change-request-${r.id}`}>
                  <div>
                    <p className="font-semibold text-slate-700">{u?.name || 'Subjekat'}</p>
                    <p className="text-xs text-slate-500">{r.description}</p>
                  </div>
                  <Button size="sm" onClick={() => approveRequest(r.id)} data-testid={`approve-request-${r.id}`}
                    className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md h-9 transition-colors duration-200">
                    <FileCheck className="h-4 w-4 mr-1.5" /> Odobri izmjenu
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AKTIVNI KORISNICI */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#f8f9fd]">
              <TableHead className="text-[#001752] font-semibold">Korisnik</TableHead>
              <TableHead className="text-[#001752] font-semibold hidden md:table-cell">E-mail</TableHead>
              <TableHead className="text-[#001752] font-semibold">Uloga</TableHead>
              <TableHead className="text-[#001752] font-semibold hidden lg:table-cell w-32">Grad</TableHead>
              {canManage && <TableHead className="w-16"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {active.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-slate-700">{u.name}</TableCell>
                <TableCell className="text-slate-500 hidden md:table-cell">{u.email}</TableCell>
                <TableCell>
                  <Badge className={`${ROLE_BADGES[u.role] || 'bg-slate-100 text-slate-600'} hover:bg-opacity-100`}>
                    {roles.find((r) => r.id === u.role)?.name || u.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-500 hidden lg:table-cell">{u.city || '-'}</TableCell>
                {canManage && (
                  <TableCell>
                    {u.role !== 'super_admin' && (
                      <Button size="icon" variant="outline" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}
                        className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="text-[#0b1a3d]">Novi korisnik sistema</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Ime i prezime *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">E-mail *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Inicijalna lozinka *</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Uloga *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="border-slate-200" data-testid="user-form-role"><SelectValue placeholder="Odaberite ulogu" /></SelectTrigger>
                <SelectContent>{roles.filter((r) => r.id !== 'turista').map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.role === 'sluzbenik' && (
              <div>
                <Label className="text-slate-600 mb-1.5 block">Grad/Općina *</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                  <SelectTrigger className="border-slate-200" data-testid="user-form-city"><SelectValue placeholder="Odaberite grad" /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={save} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="user-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
